import React, { useState, useEffect, useMemo } from 'react';
import { Layout } from '../../components/Layout/Layout';
import {
  AutoRule,
  getRules,
  saveRules,
  addRule,
  deleteRule,
  installPreset,
  BUILTIN_PRESETS,
  BUILTIN_SETS,
} from '../../data/categoriesStore';
import { getCategories } from '../../data/categoriesStore';
import styles from './Regles.module.css';

export const Regles: React.FC = () => {
  const [rules, setRules] = useState<AutoRule[]>([]);
  const [installed, setInstalled] = useState<Record<string, boolean>>({});

  // Formulaire nouvelle règle
  const [keyword, setKeyword] = useState('');
  const [newCat, setNewCat] = useState('');
  const [newSub, setNewSub] = useState('');

  const categoryList = useMemo(() => getCategories(), []);

  const reload = () => {
    const r = getRules();
    setRules(r);
    const kws = new Set(r.map((x) => x.keyword.toLowerCase()));
    const inst: Record<string, boolean> = {};
    BUILTIN_PRESETS.forEach((p) => {
      inst[p.id] = p.rules.every((rule) => kws.has(rule.keyword.toLowerCase()));
    });
    setInstalled(inst);
  };

  useEffect(() => { reload(); }, []);

  const handleAdd = () => {
    const kw = keyword.trim();
    if (!kw || !newCat) { alert('Mot-clé et catégorie sont requis.'); return; }
    if (rules.some((r) => r.keyword.toLowerCase() === kw.toLowerCase())) {
      alert('Ce mot-clé existe déjà.'); return;
    }
    addRule({ keyword: kw, category: newCat, subCategory: newSub });
    setKeyword(''); setNewSub('');
    reload();
  };

  const handleDelete = (id: string) => {
    deleteRule(id);
    reload();
  };

  const handleInstallPreset = (presetId: string) => {
    const n = installPreset(presetId);
    reload();
    if (n === 0) alert('Toutes les règles de ce préset sont déjà installées.');
    else alert(`${n} règle(s) ajoutée(s).`);
  };

  const handleUninstallPreset = (presetId: string) => {
    const preset = BUILTIN_PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    if (!window.confirm(`Supprimer toutes les règles du préset "${preset.name}" ?`)) return;
    const kws = new Set(preset.rules.map((r) => r.keyword.toLowerCase()));
    saveRules(rules.filter((r) => !kws.has(r.keyword.toLowerCase())));
    reload();
  };

  const handleInstallSet = (setId: string) => {
    const set = BUILTIN_SETS.find((s) => s.id === setId);
    if (!set) return;
    let total = 0;
    set.presetIds.forEach((pid) => { total += installPreset(pid); });
    reload();
    alert(total > 0 ? `${total} règle(s) ajoutée(s) depuis l'ensemble.` : 'Toutes les règles étaient déjà installées.');
  };

  const subList = useMemo(
    () => categoryList.find((c) => c.name === newCat)?.subCategories ?? [],
    [categoryList, newCat]
  );

  return (
    <Layout>
      <div className={styles.container}>
        <h1 className={styles.title}>Règles d'auto-catégorisation</h1>
        <p className={styles.subtitle}>
          Définissez des règles : si un mot-clé est trouvé dans le nom d'un produit, la catégorie est assignée automatiquement.
        </p>

        {/* ── ENSEMBLES ── */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Ensembles de présets</h2>
          <p className={styles.sectionDesc}>Installez un groupe de règles complet en un clic.</p>
          <div className={styles.setsGrid}>
            {BUILTIN_SETS.map((set) => {
              const allInstalled = set.presetIds.every((pid) => installed[pid]);
              return (
                <div key={set.id} className={`${styles.setCard} ${allInstalled ? styles.setCardDone : ''}`}>
                  <div className={styles.setIcon}>{allInstalled ? '✅' : '📦'}</div>
                  <div className={styles.setInfo}>
                    <div className={styles.setName}>{set.name}</div>
                    <div className={styles.setDesc}>{set.description}</div>
                    <div className={styles.setPresets}>
                      {set.presetIds.map((pid) => {
                        const p = BUILTIN_PRESETS.find((x) => x.id === pid);
                        return p ? (
                          <span key={pid} className={`${styles.presetPill} ${installed[pid] ? styles.presetPillDone : ''}`}>
                            {p.name}
                          </span>
                        ) : null;
                      })}
                    </div>
                  </div>
                  <button
                    onClick={() => handleInstallSet(set.id)}
                    className={allInstalled ? styles.btnInstalled : styles.btnInstall}
                  >
                    {allInstalled ? 'Déjà installé' : '⬇ Installer tout'}
                  </button>
                </div>
              );
            })}
          </div>
        </section>

        {/* ── PRÉSETS ── */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Présets individuels</h2>
          <p className={styles.sectionDesc}>Installez ou retirez chaque groupe de règles indépendamment.</p>
          <div className={styles.presetsGrid}>
            {BUILTIN_PRESETS.map((preset) => (
              <div key={preset.id} className={`${styles.presetCard} ${installed[preset.id] ? styles.presetCardDone : ''}`}>
                <div className={styles.presetHeader}>
                  <span className={styles.presetName}>{preset.name}</span>
                  {installed[preset.id]
                    ? <span className={styles.badgeDone}>✓ Installé</span>
                    : <span className={styles.badgePending}>Non installé</span>}
                </div>
                <p className={styles.presetDesc}>{preset.description}</p>
                <ul className={styles.presetRules}>
                  {preset.rules.map((r, i) => (
                    <li key={i}>
                      <code className={styles.kw}>"{r.keyword}"</code>
                      <span className={styles.arrow}>→</span>
                      <span className={styles.catLabel}>{r.category}</span>
                      {r.subCategory && <span className={styles.subLabel}>/ {r.subCategory}</span>}
                    </li>
                  ))}
                </ul>
                <div className={styles.presetActions}>
                  {installed[preset.id] ? (
                    <button onClick={() => handleUninstallPreset(preset.id)} className={styles.btnUninstall}>
                      🗑 Retirer
                    </button>
                  ) : (
                    <button onClick={() => handleInstallPreset(preset.id)} className={styles.btnInstall}>
                      ⬇ Installer
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── MES RÈGLES ── */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Mes règles ({rules.length})</h2>

          {/* Barre de création rapide */}
          <div className={styles.quickBar}>
            <div className={styles.quickBarLabel}>Nouvelle règle :</div>
            <input
              type="text"
              placeholder='Mot-clé (ex: "sel de nic")'
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
              className={styles.kwInput}
            />
            <span className={styles.quickArrow}>→</span>
            <select
              value={newCat}
              onChange={(e) => { setNewCat(e.target.value); setNewSub(''); }}
              className={styles.catSelect}
            >
              <option value="">Catégorie...</option>
              {categoryList.map((c) => (
                <option key={c.id} value={c.name}>{c.name}</option>
              ))}
            </select>
            <select
              value={newSub}
              onChange={(e) => setNewSub(e.target.value)}
              className={styles.catSelect}
              disabled={subList.length === 0}
            >
              <option value="">Sous-catégorie...</option>
              {subList.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <button onClick={handleAdd} className={styles.btnAdd}>+ Ajouter</button>
          </div>

          {/* Liste des règles */}
          {rules.length === 0 ? (
            <div className={styles.empty}>
              Aucune règle. Installez un préset ou ajoutez-en une manuellement.
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Mot-clé</th>
                  <th>→ Catégorie</th>
                  <th>→ Sous-catégorie</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rules.map((rule) => (
                  <tr key={rule.id}>
                    <td><code className={styles.kw}>"{rule.keyword}"</code></td>
                    <td><span className={styles.catLabel}>{rule.category}</span></td>
                    <td><span className={styles.subLabel}>{rule.subCategory || '—'}</span></td>
                    <td>
                      <button onClick={() => handleDelete(rule.id)} className={styles.btnDelete}>×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

      </div>
    </Layout>
  );
};
